import React, { useMemo, useState } from "react";
import MyModal from "../../../../Common/MyModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Form, Modal, Offcanvas } from "react-bootstrap";
import ContactSetSearch from "../../../../Contacts/ContactSetSearch";
import ContactSetsList from "../../../../Contacts/ContactSetsList";
import MyCollapse from "../../../../Common/MyCollapse";

const emptyArray: string[] = [];

export interface ContactSetInputProps {
  values: any[];
  onChange: (values: any[]) => void;
  isMultiple?: boolean;
  hideList?: boolean;
  modalAsOffcanvas?: boolean;
}

export default function ContactSetInput({
  values: p_values,
  onChange,
  isMultiple,
  hideList,
  modalAsOffcanvas = false,
}: ContactSetInputProps) {
  const values = useMemo(() => p_values.filter((v) => !!v), [p_values]);
  const [show, setShow] = useState(false);
  const [tobeRemoved, setTobeRemoved] = useState<string[]>([]);
  const [selectedSets, setSelectedSets] = useState<string[]>([]);
  const [csSearch, setCsSearch] = useState("");
  const [csType, setCsType] = useState("");
  const [orderBy, setOrderBy] = useState<string | undefined>(undefined);
  const [replace, setReplace] = useState(false);
  const PickerShell = (modalAsOffcanvas ? Offcanvas : Modal) as any;
  const PickerHeader = modalAsOffcanvas ? Offcanvas.Header : Modal.Header;
  const PickerBody = modalAsOffcanvas ? Offcanvas.Body : Modal.Body;
  const PickerTitle = modalAsOffcanvas ? Offcanvas.Title : Modal.Title;

  return (
    <>
      <div className="p-2 bg-light rounded-2">
        {values && values.length > 0 && !hideList && (
          <MyCollapse heading={`${values.length || "No"} groups selected.`}>
            <div className="mb-2">
              <div className="d-flex gap-2 flex-wrap align-items-center mb-2">
                <strong>Selected Groups</strong>
                {tobeRemoved.length > 0 && (
                  <button
                    className="btn btn-outline-danger border-0 "
                    onClick={() => {
                      onChange(values.filter((v) => !tobeRemoved.includes(v)));
                      setTobeRemoved([]);
                    }}
                  >
                    Remove selected
                  </button>
                )}
              </div>
              <ContactSetsList
                contactSetButton={(cs: any) => (
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`remove-group-checkbox-${cs.uuid}`}
                      checked={tobeRemoved.includes(cs.uuid)}
                      disabled={
                        !isMultiple &&
                        selectedSets.length > 0 &&
                        !selectedSets.includes(cs.uuid)
                      }
                      onChange={(e) => {
                        if (e.target.checked) {
                          setTobeRemoved((prev) => [...prev, cs.uuid]);
                        } else {
                          setTobeRemoved((prev) =>
                            prev.filter((id) => id !== cs.uuid)
                          );
                        }
                      }}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={`remove-group-checkbox-${cs.uuid}`}
                    ></label>
                  </div>
                )}
                search={csSearch}
                csType={csType}
                orderBy={orderBy}
                refresh={undefined}
                isModal={true}
                fullView={false}
                excludeUuids={undefined}
                uuids={values}
              />
            </div>
          </MyCollapse>
        )}
        {(isMultiple || values.length === 0 || !values?.[0]) && (
          <button className="btn btn-link p-0" onClick={() => setShow(true)}>
            Select Groups
          </button>
        )}
      </div>
      <PickerShell
        show={show}
        onHide={() => {
          setShow(false);
        }}
        {...(modalAsOffcanvas
          ? { placement: "end", style: { width: "800px" } }
          : { size: "xl" })}
      >
        <PickerHeader closeButton>
          <PickerTitle>Select Groups</PickerTitle>
        </PickerHeader>
        <PickerBody>
          <div style={{ minHeight: 720 }}>
            <div className="bg-colored-reverse p-3 rounded-3 d-flex flex-wrap align-items-end mb-3 gap-2">
              <ContactSetSearch
                search={csSearch}
                setSearch={setCsSearch}
                csType={csType}
                setCsType={setCsType}
                orderBy={orderBy}
                setOrderBy={setOrderBy}
              />
            </div>
            <ContactSetsList
              contactSetButton={(cs: any) => (
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`group-checkbox-${cs.uuid}`}
                    checked={selectedSets.includes(cs.uuid)}
                    disabled={
                      !isMultiple &&
                      selectedSets.length > 0 &&
                      !selectedSets.includes(cs.uuid)
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSets((prev) => [...prev, cs.uuid]);
                      } else {
                        setSelectedSets((prev) =>
                          prev.filter((id) => id !== cs.uuid)
                        );
                      }
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`group-checkbox-${cs.uuid}`}
                  ></label>
                </div>
              )}
              search={csSearch}
              csType={csType}
              orderBy={orderBy}
              refresh={undefined}
              isModal={true}
              fullView={false}
              excludeUuids={values}
              uuids={undefined}
            />
          </div>
          <div className="d-flex flex-row justify-content-end align-items-center mb-3 mt-2">
            <div className="form-check me-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="replace"
                checked={replace}
                onChange={(e) => setReplace(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="replace">
                Replace existing groups
              </label>
            </div>
            {isMultiple ? (
              <button
                className="btn btn-colored"
                onClick={() => {
                  if (replace) {
                    onChange(selectedSets);
                  } else {
                    onChange([...values, ...selectedSets]);
                  }
                  setShow(false);
                  setSelectedSets([]);
                  setReplace(false);
                }}
                disabled={selectedSets.length === 0}
              >
                Save
              </button>
            ) : (
              <button
                className="btn btn-colored"
                onClick={() => {
                  if (selectedSets.length > 0) {
                    onChange([selectedSets[0]]);
                  }
                  setShow(false);
                  setSelectedSets([]);
                  setReplace(false);
                }}
                disabled={selectedSets.length === 0}
              >
                Select
              </button>
            )}
          </div>
        </PickerBody>
      </PickerShell>
    </>
  );
}
